import { COLLAB_FRAGMENT_FIELD } from "@wiki/core";
import type { Plugin } from "prosemirror-state";
import { yCursorPlugin, ySyncPlugin, yUndoPlugin } from "y-prosemirror";
import type * as Y from "yjs";

import type { CollaborationProviderLike, CollaborationUser } from "./types";

export interface CollaborationOptions {
  ydoc: Y.Doc;
  provider: CollaborationProviderLike;
  user: CollaborationUser;
}

/**
 * Awareness colours are derived from the user id (`collaboration.md` §10): the
 * same person is the same colour in every session and on every machine, with
 * no assignment protocol and no server state. The palette stays legible on
 * both the light and the dark surface.
 */
export const AWARENESS_COLORS: readonly string[] = [
  "#2f6feb",
  "#c4392f",
  "#0f7b6c",
  "#a4501c",
  "#7c3aed",
  "#b3261e",
  "#1f6f8b",
  "#8a6d00",
];

export function colorForUser(userId: string): string {
  let hash = 0;
  for (const character of userId) {
    hash = (hash * 31 + (character.codePointAt(0) ?? 0)) >>> 0;
  }
  return AWARENESS_COLORS[hash % AWARENESS_COLORS.length] ?? AWARENESS_COLORS[0] ?? "#2f6feb";
}

/**
 * The collaborative plugin stack: sync on the `default` fragment (the field
 * `@wiki/core` writes), remote carets from awareness, and Yjs's own undo
 * manager — a local history plugin would undo other people's edits, so the
 * caller installs one or the other, never both.
 */
export function collaborationPlugins(options: CollaborationOptions): Plugin[] {
  const fragment = options.ydoc.getXmlFragment(COLLAB_FRAGMENT_FIELD);
  const plugins: Plugin[] = [ySyncPlugin(fragment) as Plugin];
  const awareness = options.provider.awareness;
  if (awareness) {
    awareness.setLocalStateField("user", {
      id: options.user.id,
      name: options.user.name,
      color: options.user.color,
    });
    plugins.push(
      yCursorPlugin(awareness as unknown as Parameters<typeof yCursorPlugin>[0]) as Plugin,
    );
  }
  plugins.push(yUndoPlugin() as Plugin);
  return plugins;
}
